/**
 * Table-mode SELECT / headerMap builders (shared with menu-config logic).
 */

import { Parser as SqlParser } from "node-sql-parser";
import {
  parsePrimaryQualifiedTable,
  parseSelectAstFromSql,
  parseSelectColumnsFromSql,
  resolveSqlMappingRowLabel,
  restoreFwSqlPlaceholders,
  resolveSqlMappingRowGraph,
  isDisplayNameInVisibleColumns,
  resolveSqlMappingColumnType,
  buildSqlMappingTypeByExpressionMap,
  // sqlifySelectColumnExpr,
} from "./columnMappingCore";

const sqlParser = new SqlParser();

export function buildDatePartSqlExpr(quotedCol, grain) {
  const ts = `(${quotedCol})::timestamp`;
  switch (String(grain || "").trim().toUpperCase()) {
    case "YEAR":
      return `EXTRACT(YEAR FROM ${ts})`;
    case "QUARTER":
      return `TO_CHAR(${ts}, 'YYYY"Q"Q')`;
    case "MONTH":
      return `EXTRACT(MONTH FROM ${ts})`;
    case "YYYYMM":
      return `TO_CHAR(${ts}, 'YYYYMM')`;
    default:
      return quotedCol;
  }
}

export function buildTableColumnSelectExpr(row, quotedCol) {
  const agg = (row.aggregate || "").trim().toUpperCase();
  if (agg === "COUNT_DISTINCT") {
    return `COUNT(DISTINCT ${quotedCol})`;
  }
  if (agg === "COUNT") {
    return `COUNT(${quotedCol})`;
  }
  if (row.displayType === "Metric" && agg) {
    return `${agg}(${quotedCol})`;
  }
  if (row.displayType === "Date" && agg) {
    return buildDatePartSqlExpr(quotedCol, agg);
  }
  return quotedCol;
}

export function rowUsesSelectAggregate(row) {
  if (!row) return false;
  const agg = (row.aggregate || "").trim().toUpperCase();
  if (agg === "COUNT" || agg === "COUNT_DISTINCT") return true;
  return row.displayType === "Metric" && !!agg;
}

export function tableModeRowWantsGraph(row) {
  if (!row || row.displayType !== "Metric") return false;
  const graph = String(row.graph || "").trim().toLowerCase();
  return graph === "yes" || graph === "y" || graph === "true";
}

export function buildGroupByClauseFromAssignments(assignments) {
  const dims = (assignments || []).filter((a) => !a.isMetric);
  const parts = dims
    .filter((a) => !rowUsesSelectAggregate(a.row))
    .map((a) => a.expr);
  return parts.join(", ");
}

export function assignmentNeedsGroupByClause(assignments) {
  return (assignments || []).some((a) => rowUsesSelectAggregate(a.row));
}

function parseAliasIndex(alias, kind) {
  if (typeof alias !== "string") return null;
  if (kind === "dim") {
    const m = /^dim(\d+)_name$/i.exec(alias.trim());
    return m ? Number(m[1]) : null;
  }
  const m = /^metric(\d+)$/i.exec(alias.trim());
  return m ? Number(m[1]) : null;
}

function nextIndexAfterUsed(usedSet) {
  if (!usedSet.size) return 1;
  return Math.max(...usedSet) + 1;
}

/**
 * Assign dimN_name / metricN for included rows (chip order). Reuses aliases from existingHeaderMap by display label.
 */
export function assignChartAliasesToRows(orderedRows, existingHeaderMap = {}) {
  const usedDim = new Set();
  const usedMetric = new Set();
  Object.values(existingHeaderMap || {}).forEach((v) => {
    const di = parseAliasIndex(v, "dim");
    const mi = parseAliasIndex(v, "metric");
    if (di != null) usedDim.add(di);
    if (mi != null) usedMetric.add(mi);
  });

  let nextDim = nextIndexAfterUsed(usedDim);
  let nextMetric = nextIndexAfterUsed(usedMetric);

  return orderedRows.map((row) => {
    const label = String(row.displayName || row.columnName || "").trim();
    let alias = existingHeaderMap[label];
    const colNorm = String(row.columnName || "")
      .trim()
      .toLowerCase();
    if (
      alias &&
      colNorm &&
      String(alias).trim().toLowerCase() === colNorm
    ) {
      alias = undefined;
    }
    const dimIdx = parseAliasIndex(alias, "dim");
    const metricIdx = parseAliasIndex(alias, "metric");

    if (dimIdx != null) {
      alias = `dim${dimIdx}_name`;
    } else if (metricIdx != null) {
      alias = `metric${metricIdx}`;
    } else if (row.displayType === "Metric") {
      while (usedMetric.has(nextMetric)) nextMetric++;
      alias = `metric${nextMetric}`;
      usedMetric.add(nextMetric);
      nextMetric++;
    } else {
      while (usedDim.has(nextDim)) nextDim++;
      alias = `dim${nextDim}_name`;
      usedDim.add(nextDim);
      nextDim++;
    }

    return { ...row, _chartAlias: alias };
  });
}

export function buildTableIncludedSelectAssignments(rows) {
  const assignments = [];
  for (const row of rows || []) {
    if (!row || !row._chartAlias) continue;
    const colId = String(row.columnName || "").replace(/"/g, '""');
    const quotedCol = `"${colId}"`;
    const expr = buildTableColumnSelectExpr(row, quotedCol);
    const isMetric = row.displayType === "Metric";
    assignments.push({
      row,
      expr,
      alias: row._chartAlias,
      isMetric,
    });
  }
  return assignments;
}

export function buildTableModeSqlAndHeaderMap(assignments, qualifiedTable) {
  if (!assignments.length || !qualifiedTable) {
    return { newSql: "", headerMap: {} };
  }

  const selectParts = assignments.map((a) => `${a.expr} AS ${a.alias}`);
  const headerMap = {};
  let metricNum = 0;

  for (const a of assignments) {
    const label = (a.row.displayName || a.row.columnName || "").trim();
    headerMap[label] = a.alias;
    if (a.isMetric) {
      metricNum += 1;
      if (tableModeRowWantsGraph(a.row)) {
        headerMap[`${label}#`] = `graph${metricNum}`;
      }
    }
  }

  let newSql = `SELECT ${selectParts.join(",\n        ")}\n        FROM ${qualifiedTable}`;
  if (assignmentNeedsGroupByClause(assignments)) {
    const groupBy = buildGroupByClauseFromAssignments(assignments);
    if (groupBy) newSql += ` GROUP BY ${groupBy}`;
  }

  return { newSql, headerMap };
}

export function stripTopLevelLimit(sql) {
  const trimmed = String(sql || "")
    .trim()
    .replace(/;+\s*$/g, "");
  if (!trimmed) return trimmed;

  const lower = trimmed.toLowerCase();
  let depth = 0;
  let limitStart = -1;
  for (let i = 0; i < lower.length; i++) {
    const ch = lower[i];
    if (ch === "(") depth++;
    else if (ch === ")") depth--;
    if (depth === 0 && lower.startsWith("limit", i)) {
      limitStart = i;
      break;
    }
  }
  if (limitStart === -1) return trimmed;
  return trimmed.slice(0, limitStart).trimEnd();
}

/** Inject fw_where_clause / fw_and_clause like menu-config when absent. */
export function appendDynamicClause(sql) {
  if (!sql) return "";

  const lowerSql = sql.toLowerCase();
  if (
    lowerSql.includes("fw_where_clause") ||
    lowerSql.includes("fw_and_clause")
  ) {
    return sql;
  }

  const hasWhere = /\bwhere\b/i.test(sql);
  const clause = hasWhere ? "fw_and_clause" : "fw_where_clause";

  let depth = 0;
  let splitIndex = -1;
  for (let i = 0; i < lowerSql.length; i++) {
    const ch = lowerSql[i];
    if (ch === "(") depth++;
    else if (ch === ")") depth--;
    if (depth === 0) {
      if (
        lowerSql.startsWith("group by", i) ||
        lowerSql.startsWith("order by", i) ||
        lowerSql.startsWith("having", i) ||
        lowerSql.startsWith("limit", i)
      ) {
        splitIndex = i;
        break;
      }
    }
  }

  if (splitIndex !== -1) {
    return `${sql.slice(0, splitIndex)}\n${clause}\n${sql.slice(splitIndex)}`;
  }
  return `${sql}\n${clause}`;
}

/**
 * Extract WHERE (and following ORDER BY if no GROUP BY in built) from original datasql.
 */
export function extractWhereTailFromSql(sql) {
  const orig = stripTopLevelLimit(sql);
  if (!orig) return "";
  const m = orig.match(/\bwhere\b([\s\S]*)$/i);
  if (!m) return "";
  let tail = `WHERE${m[1]}`.trim();
  tail = stripTopLevelLimit(tail);
  return tail.replace(/;+\s*$/g, "").trim();
}

/**
 * Merge rebuilt SELECT/FROM[/GROUP BY] with WHERE from saved datasql.
 */
export function mergeTableBarCoreSqlWithOriginalTail(coreSql, originalSql) {
  let sql = String(coreSql || "").trim();
  const whereTail = extractWhereTailFromSql(originalSql);
  if (whereTail) {
    if (/\bgroup\s+by\b/i.test(sql)) {
      sql = sql.replace(/(\bgroup\s+by\b)/i, `${whereTail}\n$1`);
    } else {
      sql = `${sql}\n${whereTail}`;
    }
  }
  return appendDynamicClause(sql);
}

/** Chips for custom derived metrics (metricN) not backed by a catalog mapping row. */
export function mergeDerivedChipsIntoHeaderMap(headerMap, visibleColumns) {
  const out = { ...(headerMap || {}) };
  (visibleColumns || []).forEach((chip) => {
    if (!chip) return;
    const base = String(chip.base || "").trim();
    const alias = chip.alias != null ? String(chip.alias).trim() : "";
    if (!base || !alias || out[base] != null) return;
    if (/^metric\d+$/i.test(alias) || /^graph\d+$/i.test(alias)) {
      out[base] = alias;
    }
  });
  return out;
}

export function derivedColumnsNeedGroupBy(derivedColumn = {}) {
  return Object.values(derivedColumn || {}).some((spec) => {
    if (!spec || typeof spec !== "object") return false;
    const text = String(spec.formula || spec.templateFormula || "");
    return /\b(sum|avg|count|min|max)\s*\(/i.test(text);
  });
}

export function appendGroupByToCoreSql(coreSql, groupByClause) {
  const sql = String(coreSql || "").trim();
  const gb = String(groupByClause || "").trim();
  if (!sql || !gb || /\bgroup\s+by\b/i.test(sql)) return sql;
  return `${sql} GROUP BY ${gb}`;
}

/**
 * Full table-mode payload for VizletHeader save/preview.
 */
export function buildVizletTableBarColumnLayout({
  mappingRows,
  visibleColumns,
  existingHeaderMap = {},
  datasql = "",
  derivedColumn = null,
}) {
  const qualified = parsePrimaryQualifiedTable(datasql);
  if (!qualified || !qualified.table) {
    return { headerMap: {}, tableBarSql: "", assignments: [] };
  }

  const schema = qualified.schema || "";
  const table = qualified.table;
  const qualifiedTable = schema
    ? `${schema}.${table}`
    : table;

  const rowByCatalog = new Map();
  const rowByDisplay = new Map();
  (mappingRows || []).forEach((row) => {
    if (!row) return;
    if (row.columnName) {
      rowByCatalog.set(String(row.columnName).trim().toLowerCase(), row);
    }
    const key = String(row.displayName || "")
      .trim()
      .toLowerCase();
    if (key) rowByDisplay.set(key, row);
  });

  const ordered = [];
  (visibleColumns || []).forEach((chip) => {
    if (!chip) return;
    let row = null;
    const catalog = String(chip.catalogKey || "")
      .trim()
      .toLowerCase();
    if (catalog) row = rowByCatalog.get(catalog);
    if (!row) {
      const key = String(chip.base || "")
        .trim()
        .toLowerCase();
      row = rowByDisplay.get(key);
    }
    if (row && row.include) ordered.push(row);
  });

  const withAliases = assignChartAliasesToRows(ordered, existingHeaderMap);
  const assignments = buildTableIncludedSelectAssignments(withAliases);
  let { newSql, headerMap } = buildTableModeSqlAndHeaderMap(
    assignments,
    qualifiedTable,
  );
  headerMap = mergeDerivedChipsIntoHeaderMap(headerMap, visibleColumns);

  if (derivedColumnsNeedGroupBy(derivedColumn)) {
    const groupBy = buildGroupByClauseFromAssignments(assignments);
    newSql = appendGroupByToCoreSql(newSql, groupBy);
  }

  const tableBarSql = mergeTableBarCoreSqlWithOriginalTail(newSql, datasql);

  return { headerMap, tableBarSql, assignments };
}

function tableModeRowWantsGraphFromSqlRow(row) {
  if (!row || row.type !== "Metric") return false;
  const graph = String(row.graph || "").trim().toLowerCase();
  return graph === "yes" || graph === "y" || graph === "true";
}

function mappingRowByLabel(rows, label) {
  const key = String(label || "")
    .trim()
    .toLowerCase();
  if (!key) return null;
  return (
    (rows || []).find(
      (r) =>
        r &&
        String(r.label || "")
          .trim()
          .toLowerCase() === key,
    ) || null
  );
}

function mappingRowByExpr(rows, expr) {
  const key = String(expr || "")
    .trim()
    .toLowerCase();
  if (!key) return null;
  return (
    (rows || []).find(
      (r) =>
        r &&
        String(r.expr || "")
          .trim()
          .toLowerCase() === key,
    ) || null
  );
}

function resolveSqlRowShowState({ label, visibleColumns, prevByExpr }) {
  const inVisible = isDisplayNameInVisibleColumns(label, visibleColumns);
  const isNewColumn = !prevByExpr;
  if (isNewColumn) {
    return inVisible;
  }
  if (Array.isArray(visibleColumns) && visibleColumns.length) {
    return inVisible;
  }
  return prevByExpr.show != null ? !!prevByExpr.show : false;
}

/**
 * Rebuild mapping rows after user edits alias-free SQL (VizletHeader Parse).
 */
export function syncSqlModeMappingRowsFromSql({
  sql = "",
  headerMap = {},
  visibleColumns = [],
  existingRows = [],
  aliasedDatasql = "",
}) {
  const parsed = parseSelectColumnsFromSql(sql);
  if (!parsed.length) return [];

  const typeByExpr = aliasedDatasql
    ? buildSqlMappingTypeByExpressionMap(aliasedDatasql)
    : new Map();

  return parsed.map((item, idx) => {
    const expression = item.expression || "";
    const prevByExpr = mappingRowByExpr(existingRows, expression);
    const prev =
      prevByExpr ||
      existingRows[idx] ||
      mappingRowByLabel(existingRows, visibleColumns[idx]?.base) ||
      {};
    const catalogKey = item.alias || expression;

    let label = resolveSqlMappingRowLabel(prev, item, expression);

    const chip = visibleColumns[idx];
    if (chip && chip.base) {
      label = chip.base;
    } else {
      const headerKey = Object.keys(headerMap || {}).find((k) => {
        const alias = headerMap[k];
        return (
          String(alias || "").toLowerCase() ===
            String(item.alias || "").toLowerCase() ||
          String(k).toLowerCase() === String(label).toLowerCase()
        );
      });
      if (headerKey) label = headerKey;
    }

    const show = resolveSqlRowShowState({
      label,
      visibleColumns,
      prevByExpr,
    });

    const type = resolveSqlMappingColumnType({
      item,
      expression,
      label,
      headerMap,
      prev: prevByExpr || prev,
      typeHint: typeByExpr.get(
        String(expression || "")
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase(),
      ),
    });
    const headerAlias =
      label && headerMap[label] != null ? String(headerMap[label]).trim() : "";
    const aliasKey =
      prev._aliasKey ||
      (item.alias ? String(item.alias).replace(/^["']|["']$/g, "") : "") ||
      headerAlias ||
      "";
    const isMetricColumn = type === "Metric";
    const graph = isMetricColumn
      ? resolveSqlMappingRowGraph(headerMap, {
          label,
          headerDisplayKey: label,
          metricAlias: headerAlias,
          aliasKey,
          prevGraph: prevByExpr?.graph ?? prev.graph,
        })
      : "no";

    return {
      required: `Col${idx + 1}`,
      expr: expression,
      label,
      type,
      graph,
      filterable: !!prev.filterable,
      show,
      _catalogKey: catalogKey,
      _aliasKey: aliasKey,
      _headerDisplayKey: label,
      _isMetricColumn: type === "Metric",
    };
  });
}

/** Assign dimN_name / metricN (and graphN) from mapping row Type + existing header map. */
function assignSqlModeChartAliases(rows, existingHeaderMap = {}) {
  const usedDim = new Set();
  const usedMetric = new Set();
  Object.values(existingHeaderMap || {}).forEach((v) => {
    const di = parseAliasIndex(v, "dim");
    const mi = parseAliasIndex(v, "metric");
    if (di != null) usedDim.add(di);
    if (mi != null) usedMetric.add(mi);
  });

  let nextDim = nextIndexAfterUsed(usedDim);
  let nextMetric = nextIndexAfterUsed(usedMetric);

  return (rows || []).map((row) => {
    const label = String(row.label || row.expr || "").trim();
    const isMetric =
      String(row.type || "")
        .trim()
        .toLowerCase() === "metric";
    const wantsGraph = isMetric && tableModeRowWantsGraphFromSqlRow(row);

    let alias = existingHeaderMap[label];
    let dimIdx = parseAliasIndex(alias, "dim");
    let metricIdx = parseAliasIndex(alias, "metric");

    let graphAlias = null;
    if (wantsGraph) {
      const graphFromMap = existingHeaderMap[`${label}#`];
      const gm = /^graph(\d+)$/i.exec(String(graphFromMap || "").trim());
      if (gm) graphAlias = `graph${gm[1]}`;
    }

    if (isMetric) {
      if (metricIdx != null) {
        alias = `metric${metricIdx}`;
        usedMetric.add(metricIdx);
        if (wantsGraph && !graphAlias) graphAlias = `graph${metricIdx}`;
      } else {
        while (usedMetric.has(nextMetric)) nextMetric++;
        alias = `metric${nextMetric}`;
        usedMetric.add(nextMetric);
        if (wantsGraph && !graphAlias) graphAlias = `graph${nextMetric}`;
        nextMetric++;
      }
    } else if (dimIdx != null) {
      alias = `dim${dimIdx}_name`;
      usedDim.add(dimIdx);
    } else {
      while (usedDim.has(nextDim)) nextDim++;
      alias = `dim${nextDim}_name`;
      usedDim.add(nextDim);
      nextDim++;
    }

    return { row, alias, graphAlias, isMetric, wantsGraph };
  });
}

function finalizeSqlModeProductionQuery(coreSql, originalDatasql) {
  const original = String(originalDatasql || "").trim();
  let sql = stripTopLevelLimit(String(coreSql || "").trim());
  if (!sql) return "";
  sql = mergeTableBarCoreSqlWithOriginalTail(sql, original);
  return restoreFwSqlPlaceholders(sql, original);
}

/**
 * SQL-mode save/preview/API: rebuild SELECT with dimN_name / metricN from row Type,
 * merge WHERE + fw_where_clause / fw_and_clause from original production datasql.
 */
export function buildVizletSqlModeColumnLayout({
  rawSql = "",
  mappingRows = [],
  visibleColumns = [],
  existingHeaderMap = {},
  originalDatasql = "",
}) {
  const sql = String(rawSql || "").trim();
  const original = String(originalDatasql || "").trim();
  if (!sql) {
    return { headerMap: {}, datasqlToSave: "", tableBarSql: "" };
  }

  const rows = (mappingRows || []).filter(
    (r) => r && String(r.expr || "").trim(),
  );
  if (!rows.length) {
    const fallback = finalizeSqlModeProductionQuery(sql, original);
    return {
      headerMap: { ...(existingHeaderMap || {}) },
      datasqlToSave: fallback,
      tableBarSql: fallback,
    };
  }

  const ast = parseSelectAstFromSql(sql);
  if (!ast || !Array.isArray(ast.columns)) {
    const fallback = finalizeSqlModeProductionQuery(sql, original);
    return {
      headerMap: { ...(existingHeaderMap || {}) },
      datasqlToSave: fallback,
      tableBarSql: fallback,
    };
  }

  const assigned = assignSqlModeChartAliases(rows, existingHeaderMap);
  const headerEntries = [];
  const newColumns = [];

  assigned.forEach(({ row, alias, graphAlias, wantsGraph }, idx) => {
    const colAst = ast.columns[idx] || null;
    const exprAst =
      colAst && colAst.expr
        ? colAst.expr
        : {
            type: "column_ref",
            table: null,
            column: String(row.expr || "").replace(/^"|"$/g, ""),
            collate: null,
          };

    const label = row.label || row.expr;
    newColumns.push({ expr: exprAst, as: alias });
    headerEntries.push({
      key: label,
      value: alias,
      graphKey: wantsGraph && graphAlias ? `${label}#` : null,
      graphValue: graphAlias,
    });
  });

  ast.columns = newColumns;
  let coreSql;
  try {
    coreSql = sqlParser.sqlify(ast, { database: "PostgreSQL" });
  } catch {
    coreSql = sql;
  }

  const newSql = finalizeSqlModeProductionQuery(coreSql, original);
  const headerMap = {};
  headerEntries.forEach((e) => {
    headerMap[e.key] = e.value;
  });
  headerEntries.forEach((e) => {
    if (e.graphKey && e.graphValue) {
      headerMap[e.graphKey] = e.graphValue;
    }
  });

  const headerMapFinal = mergeDerivedChipsIntoHeaderMap(
    headerMap,
    visibleColumns,
  );

  return {
    headerMap: headerMapFinal,
    datasqlToSave: newSql,
    tableBarSql: newSql,
  };
}
